import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { fetchAuthProviders } from '../lib/apiClient';
import { LoginBodySchema, authKeys } from '../lib/api/auth.gen';
import { OAuthProviderButtons } from '../components/auth/OAuthProviderButtons';
import { Form, FormError, FormField, FormSubmit, Input, Stack, useZodForm } from '../components/ui';
import { applyBrandingOverlay, EMPTY_BRANDING_OVERLAY } from '../lib/branding';

const looksLikeEmail = (value: string): boolean => /.+@.+\..+/.test(value);

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  email_not_verified: 'Your provider email is unverified, so we could not link your account.',
  google_failed: 'Google sign-in did not complete. Please try again.',
  oauth_failed: 'Sign-in did not complete. Please try again.',
};

export function LoginPage(): JSX.Element {
  const { login, error, loading } = useAuth();
  // Resolved once the email looks valid (on blur) — drives per-org sign-in
  // method gating (story 1F-2). Empty until then, so the defaults show.
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [searchParams] = useSearchParams();
  const oauthErrorCode = searchParams.get('error');
  const oauthError = oauthErrorCode
    ? OAUTH_ERROR_MESSAGES[oauthErrorCode] ?? OAUTH_ERROR_MESSAGES.oauth_failed
    : null;

  // G-26 (story 2-95b): resolver schema imported from the generated client
  // (`lib/api/auth.gen.ts`), used verbatim — this form's two fields are
  // exactly the wire body, no composition needed.
  const form = useZodForm(LoginBodySchema, { defaultValues: { email: '', password: '' } });

  // Sign-in methods this org allows. Shares OAuthProviderButtons' query key so
  // one request drives both the OAuth buttons and the password-form gate. While
  // loading/undefined, password stays available (never lock someone out on a
  // transient miss).
  const { data: resolution } = useQuery({
    // Generated `auth.gen.ts` factory (G-19) — same shape
    // `OAuthProviderButtons` keys on, so the two share one cache entry.
    queryKey: authKeys.list({ email: resolvedEmail }),
    queryFn: () => fetchAuthProviders(resolvedEmail || undefined),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const passwordEnabled = !resolution || resolution.providers.some((provider) => provider.key === 'password');

  const resolveEmail = (value: string) => {
    const next = value.trim().toLowerCase();
    if (looksLikeEmail(next)) setResolvedEmail(next);
    // Story 6-9: previously a no-op when the field no longer looked like an
    // email — harmless when only `providers` rode on this state (stale
    // gating just kept the LAST resolved org's sign-in methods showing), but
    // now that pre-auth BRANDING also rides on it, leaving `resolvedEmail`
    // stuck would leave a stranger's brand painted on this form with no way
    // to clear it short of navigating away. Resets so clearing/editing the
    // email field un-brands immediately, same as it was never resolved.
    else setResolvedEmail('');
  };

  // Story 6-9 scope item 3 — pre-auth branding, resolved from the SAME email
  // hint that already resolves sign-in methods (1F-2). Applied directly, not
  // cached (`cacheBrandingOverlay` is reserved for the AUTHENTICATED user's
  // own confirmed org — caching an unauthenticated guess here would risk
  // painting a stranger's brand on this browser's NEXT real boot before that
  // user's own fetch resolves). Cleared on unmount so navigating away (to
  // /signup, or back to /login with the email field cleared) never leaves a
  // resolved org's brand ambiently applied outside this form — the same
  // "logout clears the overlay" hygiene `capabilities.tsx` already practices
  // post-auth, applied here to the pre-auth path.
  useEffect(() => {
    applyBrandingOverlay(resolution?.branding ?? EMPTY_BRANDING_OVERLAY);
    return () => applyBrandingOverlay(EMPTY_BRANDING_OVERLAY);
  }, [resolution?.branding]);

  const handleSubmit = async (values: z.input<typeof LoginBodySchema>) => {
    await login({ email: values.email, password: values.password });
  };

  return (
    // `max-w-md` is a deliberate, reasoned exception, not the ExternalLayout
    // recipe's `Container` — this page already renders inside PublicLayout's
    // own `<main>` (marketing header/footer + max-w-6xl + py-16/24), so a
    // second full ExternalLayout(py-10) here would double the vertical
    // padding, and P10 §2.6's own ExternalLayout doc comment names only
    // PublicLayout's/SharedReportPage's still-literal widths as the gap this
    // story closes — a two-field auth card stretched to the 42rem "reading"
    // measure would be a real, unrequested visual regression, not a fix.
    <Stack gap={8} className="mx-auto max-w-md">
      <Stack gap={3} className="text-center">
        <h1 className="text-3xl font-semibold text-foreground">Welcome back 👋</h1>
        <p className="text-sm text-muted-foreground">
          Log in to resume your inspections. New here?{' '}
          <Link to="/signup" className="text-primary hover:underline">
            Create an account
          </Link>
          .
        </p>
      </Stack>

      <Form form={form} onSubmit={handleSubmit} className="rounded-hairline border border-border bg-card space-y-4 p-6">
        <FormField name="email" label="Email" required>
          {(field) => (
            <Input
              type="email"
              className="w-full"
              placeholder="you@agency.com"
              {...field}
              onBlur={(event) => {
                field.onBlur();
                resolveEmail(event.target.value);
              }}
            />
          )}
        </FormField>

        {passwordEnabled ? (
          <>
            <FormField name="password" label="Password" required>
              {(field) => <Input type="password" className="w-full" placeholder="••••••••" {...field} />}
            </FormField>
            <div className="text-right text-xs">
              <Link to="/forgot-password" className="text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
          </>
        ) : (
          <p className="rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            Password sign-in is turned off for your organization. Use one of the options below.
          </p>
        )}

        <FormError message={error ?? oauthError ?? null} />

        {passwordEnabled && (
          <FormSubmit className="w-full" disabled={loading} pendingLabel="Signing in…">
            Log in
          </FormSubmit>
        )}

        <OAuthProviderButtons intent="login" email={resolvedEmail || undefined} />
      </Form>
    </Stack>
  );
}
